import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash2 } from 'lucide-react';

const StepDescription = ({ listing, patchDraft, onNext, saving }) => {
  const [title, setTitle] = useState(listing.title || '');
  const [desc, setDesc] = useState(listing.description_long || '');
  const [features, setFeatures] = useState(listing.top_features?.length ? listing.top_features : ['', '', '']);

  const addFeature = () => {
    if (features.length < 5) setFeatures([...features, '']);
  };
  const updateFeature = (i, v) => {
    const next = [...features]; next[i] = v; setFeatures(next);
  };
  const removeFeature = (i) => {
    if (features.length > 3) setFeatures(features.filter((_, j) => j !== i));
  };

  const nonEmptyFeatures = features.filter((f) => (f || '').trim().length > 0);
  const canContinue = title.trim() && desc.trim() && nonEmptyFeatures.length >= 3;

  const save = async () => {
    await patchDraft({
      title: title.trim(),
      description_long: desc.trim(),
      top_features: nonEmptyFeatures,
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">Write the story</h1>
        <p className="mt-1 text-sm text-slate-600">
          Describe the place like you'd describe it to a friend. Students decide in 10 seconds — make it count.
        </p>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Title</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={80}
          placeholder="Bright basement suite near UTM, utilities included"
          className="h-12"
          data-testid="listing-title"
        />
        <p className="text-xs text-slate-500 mt-1">{title.length}/80</p>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">
          Top features <span className="text-xs font-normal text-slate-500">(3–5 short lines)</span>
        </Label>
        <div className="space-y-2">
          {features.map((f, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={f}
                onChange={(e) => updateFeature(i, e.target.value)}
                maxLength={50}
                placeholder="e.g. 5-min walk to bus stop"
                className="flex-1 h-11"
                data-testid={`feature-${i}`}
              />
              {features.length > 3 && (
                <button
                  type="button"
                  onClick={() => removeFeature(i)}
                  className="h-11 w-11 flex items-center justify-center text-rose-600 hover:bg-rose-50 rounded-lg"
                  data-testid={`feature-remove-${i}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {features.length < 5 && (
            <button
              type="button"
              onClick={addFeature}
              className="inline-flex items-center text-xs font-medium text-amber-700 hover:text-amber-900"
              data-testid="feature-add-btn"
            >
              <Plus className="h-3.5 w-3.5 mr-1" /> Add another
            </button>
          )}
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Full description</Label>
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          maxLength={2000}
          rows={8}
          placeholder="Tell students about the place, the neighborhood, what's walking distance, who you are as a landlord, and what kind of person would thrive here."
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none leading-relaxed"
          data-testid="listing-description"
        />
        <p className="text-xs text-slate-500 mt-1">{desc.length}/2000</p>
      </div>

      <Button
        className="w-full btn-primary h-12 text-base"
        disabled={!canContinue || saving}
        onClick={save}
        data-testid="step-description-continue"
      >
        Continue
      </Button>
    </div>
  );
};

export default StepDescription;
